'use client';

import { c as _c } from "react/compiler-runtime";
import type { PageChatEngine } from '@/app/(chat)/api/chat/code-session/page-chat';
import type { AiContext } from '@/components/chat/types/ai-context';
import { useQuery } from '@tanstack/react-query';
import { pageChatEngineQueryKey } from './query-keys';
import { codeSessionRequest, jsonBody } from '@/data/code-sessions/runtime/cloud';
import { useHasCodeAccessOnInstallation } from '@/data/code-sessions/use-has-code-access';
import { isPrPageSessionInstallation } from '@/lib/code-sessions/pr-page-rollout';
import { usePrStore } from '@/lib/stores/pr-store';
const PAGE_CHAT_ROUTE = '/api/chat/code-session';

/** The agent a pull request page's chat runs on, as the page chat route chose it. */
export type PageChatEngineChoice = {
  engine: 'code_session';
  sessionId: string | null;
} | {
  engine: 'chat';
};

/**
 * Which agent the page's sidebar chats with (`resolvePageChatEngine`), or
 * undefined until it is known. Outside cubic's own installations
 * (`isPrPageSessionInstallation`), someone /code does not serve on the
 * installation, or on a pull request no installation covers, keeps the
 * sidebar's own chat at once, without asking the server. An initial check
 * failure with no cached choice falls back to the sidebar's own chat;
 * transient refresh failures retain that pull request's last good choice.
 */
export function usePageChatEngine(input) {
  const $ = _c(15);
  let t0;
  if ($[0] !== input.installationId) {
    t0 = input.installationId !== null && isPrPageSessionInstallation(input.installationId);
    $[0] = input.installationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const rolledOut = t0;
  const metadataEnabled = rolledOut && input.enabled !== false && input.pullRequestId !== null;
  let t1;
  if ($[2] !== metadataEnabled) {
    t1 = {
      enabled: metadataEnabled
    };
    $[2] = metadataEnabled;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const hasCodeAccess = useHasCodeAccessOnInstallation(input.installationId, t1);
  const enabled = metadataEnabled && hasCodeAccess === true;
  let t2;
  if ($[4] !== input.pullRequestId) {
    t2 = pageChatEngineQueryKey(input.pullRequestId);
    $[4] = input.pullRequestId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== input.pullRequestId) {
    t3 = async t4 => {
      const {
        signal
      } = t4;
      const result = await codeSessionRequest(`${PAGE_CHAT_ROUTE}?pullRequestId=${encodeURIComponent(input.pullRequestId ?? "")}`, {
        signal
      });
      if (result.engine === "chat" && (result.reason === "github_unavailable" || result.reason === "unavailable")) {
        throw new Error("Page chat engine temporarily unavailable");
      }
      return result;
    };
    $[6] = input.pullRequestId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== enabled || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      enabled,
      placeholderData: undefined,
      staleTime: 30000,
      refetchInterval: 30000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: false
    };
    $[8] = enabled;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const query = useQuery(t4);
  if (!rolledOut || hasCodeAccess === false || input.pullRequestId === null) {
    let t5;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        engine: "chat"
      };
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    return t5;
  }
  if (hasCodeAccess === undefined || query.isPending) {
    return;
  }
  let t5;
  if ($[13] !== query.data) {
    t5 = query.data?.engine === "code_session" ? {
      engine: "code_session",
      sessionId: query.data.sessionId
    } : {
      engine: "chat"
    };
    $[13] = query.data;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}

/**
 * Asks which agent the page's chat runs on as soon as the sidebar is open, beside the chat's own
 * context, so the chat has the answer when it shows (`usePageChatEngine` shares it).
 */
export function usePrefetchPageChatEngine(sidebarOpen) {
  const $ = _c(4);
  const {
    installationId,
    pullRequestId
  } = usePrStore();
  let t0;
  if ($[0] !== installationId || $[1] !== pullRequestId || $[2] !== sidebarOpen) {
    t0 = {
      installationId,
      pullRequestId,
      enabled: sidebarOpen
    };
    $[0] = installationId;
    $[1] = pullRequestId;
    $[2] = sidebarOpen;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  usePageChatEngine(t0);
}

/** A message the page's chat sends to its session (`POST /api/chat/code-session`). */
export type PageChatMessage = {
  sessionId: string;
  start: boolean;
  turnId: string;
  text: string;
  delivery: 'queue' | 'steer';
  model: string | undefined;
  pullRequestId: string;
  context: AiContext;
};

/** Sends the message, starting the session with it when it has none yet. */
export function sendPageChatMessage(message: PageChatMessage) {
  return codeSessionRequest<{
    sessionId: string;
    created: boolean;
  }>(PAGE_CHAT_ROUTE, jsonBody(message));
}